import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload } from 'lucide-react';
import { getResourceConfig, DEFAULT_RESOURCE_ACCEPT } from '@/config/resourceConfig';
import { formatFileSize } from '@/utils/fileUtils';

const ResourceUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  resourceType,
  file,
  setFile,
  isUploading,
  checkDuplicate
}) => {
  if (!isOpen) return null;

  const config = getResourceConfig(resourceType);
  const acceptTypes = config.acceptedFileTypes || DEFAULT_RESOURCE_ACCEPT;
  const files = file ? (Array.isArray(file) ? file : [file]) : [];

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFile(selectedFiles.length === 1 ? selectedFiles[0] : selectedFiles);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader>
          <CardTitle>{config.modalTitle || 'Upload Resource'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Files (Multiple files allowed)
            </label>
            <Input
              type="file"
              multiple
              onChange={handleFileChange}
              accept={acceptTypes}
            />
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-sm font-medium text-gray-700">
                  Selected {files.length} file{files.length !== 1 ? 's' : ''}:
                </p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {files.map((f, index) => {
                    const isDuplicate = checkDuplicate && checkDuplicate(f.name);
                    return (
                      <div 
                        key={index} 
                        className={`text-sm p-2 rounded ${
                          isDuplicate 
                            ? 'bg-yellow-50 border border-yellow-200' 
                            : 'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isDuplicate ? 'text-yellow-800' : 'text-gray-600'}`}>
                            {f.name}
                          </span>
                          {isDuplicate && (
                            <span className="text-xs bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded">
                              Already exists
                            </span>
                          )}
                          <span className={`ml-auto ${isDuplicate ? 'text-yellow-600' : 'text-gray-500'}`}>
                            ({formatFileSize(f.size)})
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {checkDuplicate && files.some(f => checkDuplicate(f.name)) && (
                  <p className="text-xs text-yellow-700 mt-2">
                    ⚠️ Duplicate files will be skipped during upload
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
        <div className="flex justify-end gap-3 p-6 pt-0">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={onUpload}
            disabled={files.length === 0 || isUploading}
            className="flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ResourceUploadModal;